import { useState, useRef, useEffect } from 'react';
import { Box, Button, Text } from '@chakra-ui/react';
import { useChatContext } from '@/hooks/useChatContext';
import { makeSubstitutionAction } from '@/utils/substitution';

interface Props {
  /** Full reference used in the substitution question, e.g. "2 tbsp olive oil". */
  ingredientRef: string;
  label: string;
  fontSize: string;
  fontWeight?: string | number;
  /** Weight while the popup is open (defaults to fontWeight). */
  activeFontWeight?: string | number;
  /** Recipe the ingredient belongs to — scopes the chat so a new recipe starts fresh. */
  recipeId?: string;
}

/**
 * An ingredient name the user taps to ask Chef Chat for a substitute. Owns its
 * "✦ Don't have this?" popup and the open-chat wiring; shared by the ingredient
 * list and cook mode.
 */
export default function TappableIngredient({
  ingredientRef,
  label,
  fontSize,
  fontWeight = 300,
  activeFontWeight,
  recipeId,
}: Props) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const { openChat } = useChatContext();

  // Close the popup when clicking outside this ingredient.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleAsk = () => {
    setOpen(false);
    openChat({
      // Include recipeId only when known, so openChat scopes (and resets) the
      // chat to this recipe rather than clearing scope with an undefined value.
      ...(recipeId ? { recipeId } : {}),
      pendingAction: makeSubstitutionAction(ingredientRef),
    });
  };

  return (
    <Box ref={wrapperRef} as="span" position="relative" display="inline-block" maxW="100%">
      <Text
        as="span"
        fontSize={fontSize}
        fontWeight={open ? (activeFontWeight ?? fontWeight) : fontWeight}
        color={open ? 'accent.secondary' : 'text.primary'}
        cursor="pointer"
        onClick={() => setOpen((v) => !v)}
        _hover={{ color: 'accent.secondary' }}
        transition="color 0.1s"
        style={{ textDecoration: 'underline', textDecorationColor: 'var(--chakra-colors-border-muted)', textUnderlineOffset: '3px' }}
      >
        {label}
      </Text>

      {open && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          mt="4px"
          zIndex={10}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            bg="bg.canvas"
            color="text.primary"
            borderRadius="full"
            fontFamily="heading"
            fontWeight="700"
            fontSize="xs"
            letterSpacing="-0.01em"
            borderWidth="1.5px"
            borderColor="text.primary"
            borderStyle="dashed"
            _hover={{ bg: 'mauve.100' }}
            onClick={handleAsk}
            size="sm"
            px={4}
            py={2}
            boxShadow="0 4px 12px rgba(72,60,70,0.2)"
            whiteSpace="nowrap"
          >
            ✦ Don't have this?
          </Button>
        </Box>
      )}
    </Box>
  );
}
